import { useMemo } from "react";
import { create } from "zustand";
import { userHasPermission } from "@/auth/permissions";
import { useUser } from "@/hooks/useAuth";
import type { User } from "@/types/auth";
import type { NavEntry } from "@/types/navigation";

interface SidebarStore {
  isMobileOpen: boolean;
  expandedGroups: Set<string>;

  openMobile: () => void;
  closeMobile: () => void;
  toggleGroup: (groupId: string) => void;
}

export const useSidebarStore = create<SidebarStore>((set) => ({
  isMobileOpen: false,
  expandedGroups: new Set<string>(),

  openMobile: () => {
    set({ isMobileOpen: true });
  },

  closeMobile: () => {
    set({ isMobileOpen: false });
  },

  toggleGroup: (groupId) => {
    set((state) => {
      const next = new Set(state.expandedGroups);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return { expandedGroups: next };
    });
  },
}));

function hasPermission(
  permission: string | undefined,
  user: User | null,
): boolean {
  if (!permission) {
    return true;
  }
  return userHasPermission(user, permission);
}

export function useFilteredNavItems(items: NavEntry[]): NavEntry[] {
  const user = useUser();

  return useMemo(() => {
    return items
      .filter(
        (item) =>
          (!item.adminOnly || user?.role === "Administrator") &&
          hasPermission(item.permission, user),
      )
      .map((item) => {
        if (item.kind === "group") {
          const filteredChildren = item.children.filter(
            (child) =>
              (!child.adminOnly || user?.role === "Administrator") &&
              hasPermission(child.permission, user),
          );
          if (filteredChildren.length === 0) {
            return null;
          }
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter((item): item is NavEntry => item !== null);
  }, [items, user]);
}
